import { useState, useLayoutEffect, useRef } from "react";
import { IRecords, IContractData } from "../../../types";
import { getAtmCallStrikeIndex } from "../../../calculations/strategyUtils";
import GlassyButton from "../../glassy-button";

interface IOptionChainProps {
  data: IRecords;
  selectedExpiry: string;
}

const OIChart = ({ data, selectedExpiry }: IOptionChainProps) => {
  const { underlyingValue } = data;
  const filteredData = data.data.filter((val) => val.expiryDates === selectedExpiry);
  const strikes = filteredData.map((val) => val.strikePrice);
  const atmCallIndex = getAtmCallStrikeIndex(strikes, underlyingValue);
  const startIndex = Math.max(0, atmCallIndex - 12);
  const [displayField, setDisplayField] = useState<string>("OI");
  const [maxWidth, setMaxWidth] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  const fields = ["LTP", "OI", "IV"];

  const highestOI =
    filteredData.reduce((acc, data) => {
      if (data.CE?.openInterest! > acc) acc = data.CE?.openInterest!;
      if (data.PE?.openInterest! > acc) acc = data.PE?.openInterest!;
      return acc;
    }, 1) || 1;

  const dataSource = filteredData.slice(startIndex, startIndex + 22);

  const getDisplayField = (field: string) => {
    switch (field) {
      case "LTP":
        return "lastPrice";
      case "IV":
        return "impliedVolatility";
      default:
        return "openInterest";
    }
  };

  useLayoutEffect(()=>{
    if(ref.current) {
      const { width } = ref.current.getBoundingClientRect();
      setMaxWidth(width / 2 - 85);
    }
  }, []);

  const getRoundedNumber = (value: number) => {
    if (value > 10000000) return Math.round(value / 10000000) + " C";
    else if (value > 100000) return Math.round(value / 100000) + " L";
    else if (value > 1000) return Math.round(value / 1000) + " K";
    return value;
  };

  const renderOptionChainCard = (
    value: IContractData | undefined,
    isCall: boolean
  ) => {
    if (!value) return null;
    return (
      <div
        style={{
          display: "flex",
          flexDirection: isCall ? "row-reverse" : "row",
          gap: "4px",
          width: "100%",
        }}
      >
        <div
          style={{
            height: "20px",
            width: `${(value.openInterest / highestOI) * maxWidth}px`,
            backgroundColor: isCall ? "green" : "red",
          }}
        ></div>

        <div>
          {getRoundedNumber(value[getDisplayField(displayField)]!)}
        </div>
      </div>
    );
  };
  return (
    <div>
      <div style={{ textAlign: "center" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
            alignItems: "center",
            padding: "2px 8px",
          }}
        >
          {fields.map((field) => (
            <GlassyButton
              key={field}
              text={field}
              isActive={displayField === field}
              onClick={() => setDisplayField(field)}
            />
          ))}
        </div>
      </div>
      <div
        ref={ref}
        style={{ display: "flex", flexDirection: "column", gap: "8px" }}
      >
        {dataSource.map((data, index) => {
          return (
            <div
              key={index}
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: "4px",
              }}
            >
              <div style={{ width: "calc(50% - 50px)" }}>
                {renderOptionChainCard(data.CE, true)}
              </div>
              <div style={{ width: "45px" }}>{data.strikePrice}</div>
              <div style={{ width: "calc(50% - 50px)" }}>
                {renderOptionChainCard(data.PE, false)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OIChart;
